'use client';

import React, { useState, useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';

interface TopbarProps {
  sidebarCollapsed: boolean;
  mobileMenuOpen?: boolean;
}

const notifications = [
  { id: 'notif-001', text: 'Tarea "Contactar patrocinadores Golf" vence mañana', type: 'warning', time: 'Hace 20 min' },
  { id: 'notif-002', text: 'Pago de cuota pendiente: Sesión 18 Mayo', type: 'error', time: 'Hace 1 hr' },
  { id: 'notif-003', text: 'Transform-Arte: Rock Q2 actualizado a Verde ✓', type: 'success', time: 'Hace 2 hr' },
  { id: 'notif-004', text: 'Carlos Mendoza agendó una reunión 1:1 contigo', type: 'info', time: 'Ayer' },
];

export default function Topbar({ sidebarCollapsed, mobileMenuOpen }: TopbarProps) {
  const [notifOpen, setNotifOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const { signOut, user, userProfile, userRole } = useAuth();
  const router = useRouter();
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notifMenuRef = useRef<HTMLDivElement>(null);

  // Rotary year label state
  const [rotaryYearLabel, setRotaryYearLabel] = useState('Año Rotario 2025–2026');
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [savingLabel, setSavingLabel] = useState(false);
  const editInputRef = useRef<HTMLInputElement>(null);
  const supabase = createClient();

  const isSuperusuario = userRole === 'superusuario';

  // Load rotary year label from Supabase
  useEffect(() => {
    const fetchLabel = async () => {
      const { data } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'rotary_year_label')
        .single();
      if (data?.value) {
        setRotaryYearLabel(data.value);
      }
    };
    fetchLabel();
  }, []);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingLabel && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [isEditingLabel]);

  const handleStartEdit = () => {
    setEditValue(rotaryYearLabel);
    setIsEditingLabel(true);
  };

  const handleSaveLabel = async () => {
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === rotaryYearLabel) {
      setIsEditingLabel(false);
      return;
    }
    setSavingLabel(true);
    const { error } = await supabase
      .from('app_settings')
      .upsert({ key: 'rotary_year_label', value: trimmed, updated_at: new Date().toISOString() });
    if (!error) {
      setRotaryYearLabel(trimmed);
    }
    setSavingLabel(false);
    setIsEditingLabel(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSaveLabel();
    if (e.key === 'Escape') setIsEditingLabel(false);
  };

  // Close menus on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
      if (notifMenuRef.current && !notifMenuRef.current.contains(event.target as Node)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setLoggingOut(true);
    setUserMenuOpen(false);
    try {
      await signOut();
      router.replace('/sign-up-login-screen');
    } catch (error) {
      setLoggingOut(false);
    }
  };

  const userInitials = userProfile?.full_name
    ? userProfile.full_name.split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase()
    : (user?.email?.[0]?.toUpperCase() ?? 'U');
  const userEmail = user?.email || 'Sin Correo Electrónico';

  const userName = userProfile?.full_name || user?.email || 'Usuario';
  const userRoleLabel = userRole === 'superusuario' ? 'Superusuario'
    : userRole === 'mesa_directiva' ? 'Mesa Directiva' : 'Socio Activo';

  return (
    <header
      className={`fixed top-0 right-0 bg-card border-b border-border z-30 flex items-center justify-between px-3 md:px-6 transition-all duration-300 ease-in-out left-0 ${
        sidebarCollapsed ? 'md:left-[var(--sidebar-collapsed)]' : 'md:left-[var(--sidebar-width)]'
      }`}
      style={{ height: 'var(--topbar-height)' }}
    >
      {/* Left: page context */}
      <div className="flex items-center gap-2 md:gap-3 min-w-0 pr-2">
        <div className="min-w-0 flex-1">
          {/* Rotary year label — editable by superusuario */}
          {isEditingLabel ? (
            <div className="flex items-center gap-1">
              <input
                ref={editInputRef}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={savingLabel}
                className="text-xs font-medium text-muted-foreground bg-muted border border-border rounded px-1.5 py-0.5 w-32 sm:w-44 focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
              />
              <button
                onClick={handleSaveLabel}
                disabled={savingLabel}
                className="text-primary hover:text-primary/80 disabled:opacity-50"
                title="Guardar"
              >
                <Icon name="CheckIcon" size={14} variant="outline" />
              </button>
              <button
                onClick={() => setIsEditingLabel(false)}
                disabled={savingLabel}
                className="text-muted-foreground hover:text-foreground disabled:opacity-50"
                title="Cancelar"
              >
                <Icon name="XMarkIcon" size={14} variant="outline" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1 group">
              <p className="text-[11px] sm:text-xs text-muted-foreground font-medium truncate">
                {rotaryYearLabel}
              </p>
              {isSuperusuario && (
                <button
                  onClick={handleStartEdit}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary shrink-0"
                  title="Editar año rotario"
                >
                  <Icon name="PencilIcon" size={11} variant="outline" />
                </button>
              )}
            </div>
          )}

          {/* District & Governor text - Adaptive sizes */}
          <p className="text-xs sm:text-sm font-semibold text-foreground truncate">
            <span className="hidden sm:inline">Gobernador: </span>David Eaton
            <span className="mx-1 text-muted-foreground">·</span>
            <span className="hidden xs:inline">Distrito </span>4130
          </p>
        </div>
      </div>

      {/* Right: actions */}
      <div className="flex items-center gap-1 sm:gap-2 shrink-0">
        {/* Notifications */}
        <div className="relative" ref={notifMenuRef}>
          <button
            onClick={() => setNotifOpen(!notifOpen)}
            className="relative w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center hover:bg-muted transition-all duration-150"
            aria-label="Notificaciones"
          >
            <Icon name="BellIcon" size={18} variant="outline" className="text-muted-foreground sm:w-[20px] sm:h-[20px]" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-status rounded-full" />
          </button>

          {notifOpen && (
            <div className="absolute right-0 top-11 w-[calc(100vw-2rem)] max-w-xs sm:w-80 bg-card border border-border rounded-xl shadow-modal z-50 fade-in overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <p className="text-sm font-semibold text-foreground">Notificaciones</p>
                <span className="text-xs text-muted-foreground">{notifications.length} nuevas</span>
              </div>
              <ul className="divide-y divide-border max-h-72 overflow-y-auto">
                {notifications.map((n) => (
                  <li key={n.id} className="px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer">
                    <p className="text-sm text-foreground leading-snug">{n.text}</p>
                    <p className="text-xs text-muted-foreground mt-1">{n.time}</p>
                  </li>
                ))}
              </ul>
              <div className="px-4 py-2.5 border-t border-border">
                <button className="text-xs text-primary font-semibold hover:underline">
                  Ver todas las notificaciones
                </button>
              </div>
            </div>
          )}
        </div>

        {/* User avatar with dropdown */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center gap-2 pl-1.5 sm:pl-2 border-l border-border hover:opacity-80 transition-opacity cursor-pointer"
            aria-label="Menú de usuario"
          >
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
              <span className="text-xs font-bold text-white">{userInitials}</span>
            </div>
            <div className="hidden md:block text-left">
              <p className="text-xs font-semibold text-foreground leading-none">{userName}</p>
              <p className="text-xs text-muted-foreground">{userRoleLabel}</p>
            </div>
            <Icon
              name="ChevronDownIcon"
              size={14}
              variant="outline"
              className={`text-muted-foreground hidden md:block transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {userMenuOpen && (
            <div className="absolute right-0 top-11 sm:top-12 w-52 sm:w-56 bg-card border border-border rounded-xl shadow-modal z-50 fade-in overflow-hidden">
              <div className="px-4 py-3 border-b border-border">
                <p className="text-sm font-semibold text-foreground leading-none truncate">{userName}</p>
                <p className="text-xs text-muted-foreground truncate mt-1">{userEmail}</p>
                <span className="inline-block mt-1.5 px-2 py-0.5 text-[10px] font-medium bg-muted rounded-md text-foreground">
                  {userRoleLabel}
                </span>
              </div>
              <div className="p-1.5">
                <button
                  onClick={handleLogout}
                  disabled={loggingOut}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-all duration-150 text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Icon name="ArrowRightOnRectangleIcon" size={16} variant="outline" />
                  <span>{loggingOut ? 'Saliendo...' : 'Cerrar sesión'}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}